import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Search, Plus, MoreHorizontal, CheckCircle, XCircle, Clock, UserPlus, Filter
} from 'lucide-react';

const KycBadge = ({ status }) => {
  const styles = {
    verified: 'status-verified',
    pending: 'status-pending',
    rejected: 'status-rejected'
  };
  
  const icons = {
    verified: CheckCircle,
    pending: Clock,
    rejected: XCircle
  };
  
  const Icon = icons[status] || Clock;
  
  return (
    <Badge className={`${styles[status]} gap-1`}>
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  );
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [kycFilter, setKycFilter] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    mt5_account: ''
  });

  const fetchClients = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (kycFilter !== 'all') params.kyc_status = kycFilter;
      
      const response = await api.getClients(params);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [searchQuery, kycFilter]);

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await api.createClient(newClient);
      toast.success('Client created successfully');
      setIsAddDialogOpen(false);
      setNewClient({ full_name: '', email: '', phone: '', country: '', mt5_account: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleKycUpdate = async (clientId, status) => {
    try {
      await api.updateKycStatus(clientId, status);
      toast.success(`KYC status updated to ${status}`);
      fetchClients();
    } catch (error) {
      toast.error('Failed to update KYC status');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">Manage your client accounts and KYC</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="glow-primary" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="add-client-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Add New Client
              </DialogTitle>
              <p id="add-client-description" className="text-sm text-muted-foreground">
                Enter client details to create a new account
              </p>
            </DialogHeader>
            <form onSubmit={handleAddClient} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={newClient.full_name}
                  onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })}
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={newClient.country}
                    onChange={(e) => setNewClient({ ...newClient, country: e.target.value })}
                    data-testid="client-country-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mt5_account">MT5 Account</Label>
                <Input
                  id="mt5_account"
                  value={newClient.mt5_account}
                  onChange={(e) => setNewClient({ ...newClient, mt5_account: e.target.value })}
                  placeholder="e.g., 12345678"
                  data-testid="client-mt5-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-client-btn">Create Client</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="client-search-input"
              />
            </div>
            <Select value={kycFilter} onValueChange={setKycFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="kyc-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="KYC Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Client List ({clients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No clients found</p>
              <p className="text-sm">Add your first client to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>MT5 Account</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Equity</TableHead>
                    <TableHead>KYC Status</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{client.full_name}</p>
                          <p className="text-xs text-muted-foreground">{client.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{client.mt5_account || '-'}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(client.balance)}</TableCell>
                      <TableCell className={`font-mono ${client.equity >= client.balance ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(client.equity)}
                      </TableCell>
                      <TableCell>
                        <KycBadge status={client.kyc_status} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`client-actions-${client.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleKycUpdate(client.id, 'verified')}
                              className="text-emerald-400"
                              data-testid={`verify-kyc-${client.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Verify KYC
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleKycUpdate(client.id, 'rejected')}
                              className="text-red-400"
                              data-testid={`reject-kyc-${client.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject KYC
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleKycUpdate(client.id, 'pending')}
                              data-testid={`pending-kyc-${client.id}`}
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              Set Pending
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Clients;
